import * as Api from '@ViewModels';

export type PlatformCapabilityKey = keyof Omit<Api.ISocialMediaPlatformCapabilities, 'platform'>;

/**
 * How a field collapses across several platforms. Ceilings take the smallest, floors take the
 * largest, permissions have to hold everywhere, and requirements hold if they hold anywhere — four
 * rules, not one, which is why the server owns the reduction and this client only describes it.
 * `combined` on the response is the reduction; nothing here recomputes it.
 */
export type CapabilityReduction = 'min' | 'max' | 'all' | 'any';

export interface IPlatformCapabilityField {
	key: PlatformCapabilityKey;
	label: string;
	group: 'content' | 'media';
	reduction: CapabilityReduction;
	/** Reads under the combined value: what the reduction did and why. */
	reductionNote: string;
	/** True where the API may answer "not established", which is distinct from a limit of 0. */
	nullable: boolean;
	/**
	 * What a null means for this field, which differs by kind. A limit the API has not published is
	 * simply unenforced. A capability is unavailable: nothing in the API accepts the value, so a client
	 * must not build an input for it. Only read where `nullable`.
	 */
	nullMeaning?: 'unenforced' | 'unavailable';
}

export const PLATFORM_CAPABILITY_FIELDS: readonly IPlatformCapabilityField[] = [
	{
		key: 'maxContentCharacters',
		label: 'Caption length',
		group: 'content',
		reduction: 'min',
		reductionNote: 'The tightest ceiling wins.',
		nullable: false,
	},
	{
		key: 'requiresMedia',
		label: 'Media required',
		group: 'media',
		reduction: 'any',
		reductionNote: 'One platform requiring media requires it of the whole post.',
		nullable: false,
	},
	{
		key: 'maxImages',
		label: 'Most images',
		group: 'media',
		reduction: 'min',
		reductionNote: 'The tightest ceiling wins.',
		nullable: false,
	},
	{
		key: 'maxVideos',
		label: 'Most videos',
		group: 'media',
		reduction: 'min',
		reductionNote: 'The tightest ceiling wins.',
		nullable: false,
	},
	{
		key: 'maxMediaItems',
		label: 'Most items in total',
		group: 'media',
		reduction: 'min',
		reductionNote: 'Images and videos together, whether or not the platform mixes them.',
		nullable: false,
	},
	{
		key: 'allowsImagesWithVideo',
		label: 'Images and video together',
		group: 'media',
		reduction: 'all',
		reductionNote: 'Offered only where every platform offers it.',
		nullable: false,
	},
	{
		key: 'supportsPerImageAltText',
		label: 'Alt text per image',
		group: 'media',
		reduction: 'all',
		reductionNote: 'Offered only where every platform offers it.',
		nullable: true,
		nullMeaning: 'unavailable',
	},
] as const;

export const getSupportedPlatforms = (platforms?: readonly Api.SocialMediaType[]): Api.SocialMediaType[] => {
	const requested = (platforms ?? []).filter(platform => Api.SupportedSocialMediaTypes.includes(platform));
	const deduped = Array.from(new Set(requested));
	// An empty filter is the whole set, matching how the endpoint reads `?platforms=`.
	return deduped.length ? deduped : [...Api.SupportedSocialMediaTypes];
};

/**
 * One platform's limits out of a response, or undefined before the response arrives. There is no
 * local stand-in on purpose: the API is the only place these figures are written down, and a
 * plausible-looking guess here would let the composer accept a post the publish path rejects.
 */
export const findPlatformCapabilities = (
	response: Api.ISocialMediaPlatformCapabilitiesResponse | undefined,
	platform: Api.SocialMediaType
): Api.ISocialMediaPlatformCapabilities | undefined =>
	response?.platforms?.find(capability => capability.platform === platform);

/**
 * Which platforms actually produced the combined value — what makes the reduction legible
 * ("2,200 because of Instagram") rather than a number with no owner.
 */
export const getBindingPlatforms = (
	field: IPlatformCapabilityField,
	capabilities: readonly Api.ISocialMediaPlatformCapabilities[],
	combined: Api.ISocialMediaCombinedPlatformCapabilities
): Api.SocialMediaType[] => {
	const combinedValue = combined[field.key];
	if (field.reduction === 'any') {
		return combinedValue === true
			? capabilities.filter(capability => capability[field.key] === true).map(capability => capability.platform)
			: [];
	}
	if (field.reduction === 'all') {
		return combinedValue === true
			? []
			: capabilities.filter(capability => capability[field.key] !== true).map(capability => capability.platform);
	}
	if (combinedValue === null || combinedValue === undefined) {
		return [];
	}
	return capabilities
		.filter(capability => capability[field.key] === combinedValue)
		.map(capability => capability.platform);
};

const NOT_ESTABLISHED_LABEL = 'Not enforced';

/**
 * Distinct from {@link NOT_ESTABLISHED_LABEL}: the API returns null for a capability it cannot carry at
 * all, and reading that as an unenforced limit would say the opposite of what it means.
 */
const NOT_AVAILABLE_LABEL = 'Not supported';

export const formatCapabilityValue = (
	field: IPlatformCapabilityField,
	value: Api.ISocialMediaPlatformCapabilities[PlatformCapabilityKey]
): string => {
	if (value === null || value === undefined) {
		return field.nullMeaning === 'unavailable' ? NOT_AVAILABLE_LABEL : NOT_ESTABLISHED_LABEL;
	}
	if (typeof value === 'boolean') {
		return value ? 'Yes' : 'No';
	}
	return value.toLocaleString();
};
